import { useEffect, useState, type FormEvent } from 'react';
import { backend } from '../data';
import type { Session, UserInvitePeek } from '@bhorosha/data';
import { t, tErr } from '@bhorosha/i18n';
import { AuthCard, ErrorText, Field, Notice, PasswordInput, Spinner } from '../ui/components';

/** First visit to a brand-new site: create the separate super admin login. */
export function SetupAdminScreen({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function submit(e: FormEvent) {
    e.preventDefault(); setErr('');
    if (pw.length < 8) { setErr(t('auth.err.short')); return; }
    if (pw !== pw2) { setErr(t('auth.err.mismatch')); return; }
    setBusy(true);
    try {
      await backend.bootstrapAdmin(name, email, pw);
      await backend.signIn(email.trim(), pw);
      onDone();
    } catch (ex) { setErr(tErr(ex)); } finally { setBusy(false); }
  }

  return (
    <AuthCard>
      <h1 className="bh-h1 bh-auth-title">{t('setup.title')}</h1>
      <p className="bh-auth-sub">{t('setup.sub')}</p>
      <Notice>{t('setup.note')}</Notice>
      <form className="bh-form" onSubmit={submit}>
        <Field label={t('setup.name')}><input value={name} onChange={e => setName(e.target.value)} placeholder="Admin" required /></Field>
        <Field label={t('setup.email')} hint={t('setup.emailHint')}><input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@example.com" autoComplete="username" required /></Field>
        <Field label={t('auth.password')}><PasswordInput value={pw} onChange={setPw} strength /></Field>
        <Field label={t('auth.passwordAgain')}><PasswordInput value={pw2} onChange={setPw2} matchWith={pw} /></Field>
        <ErrorText>{err}</ErrorText>
        <button type="submit" className="bh-btn bh-btn-primary bh-full bh-btn-lg" disabled={busy}>{busy ? t('common.loading') : t('setup.create')}</button>
      </form>
    </AuthCard>
  );
}

/**
 * Signed in with a one-time password from the admin: the user must choose
 * their own before anything else (the database blocks all data until then).
 */
export function TempPasswordScreen({ session, expired, onDone }: { session: Session; expired: boolean; onDone: () => void }) {
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function submit(e: FormEvent) {
    e.preventDefault(); setErr('');
    if (pw.length < 8) { setErr(t('auth.err.short')); return; }
    if (pw !== pw2) { setErr(t('auth.err.mismatch')); return; }
    setBusy(true);
    try { await backend.finishTempPassword(pw); onDone(); }
    catch (ex) { setErr(tErr(ex)); } finally { setBusy(false); }
  }
  const signOut = () => { void backend.signOut(); };

  if (expired) {
    return (
      <AuthCard>
        <h1 className="bh-h1 bh-auth-title">{t('temp.expiredTitle')}</h1>
        <Notice tone="warn">{t('temp.expired')}</Notice>
        <button className="bh-btn bh-btn-primary bh-full" onClick={signOut}>{t('menu.signOut')}</button>
      </AuthCard>
    );
  }
  return (
    <AuthCard>
      <h1 className="bh-h1 bh-auth-title">{t('temp.title')}</h1>
      <p className="bh-auth-sub">{t('temp.sub')}</p>
      <form className="bh-form" onSubmit={submit}>
        <Field label={t('auth.email')}><input type="email" value={session.email} readOnly autoComplete="username" /></Field>
        <Field label={t('temp.new')}><PasswordInput value={pw} onChange={setPw} autoFocus strength autoComplete="new-password" /></Field>
        <Field label={t('auth.passwordAgain')}><PasswordInput value={pw2} onChange={setPw2} matchWith={pw} autoComplete="new-password" /></Field>
        <ErrorText>{err}</ErrorText>
        <button type="submit" className="bh-btn bh-btn-primary bh-full bh-btn-lg" disabled={busy}>{busy ? t('common.loading') : t('temp.save')}</button>
      </form>
      <div className="bh-auth-foot"><button className="bh-link" onClick={signOut}>{t('menu.signOut')}</button></div>
    </AuthCard>
  );
}

export function welcomeTokenFromUrl(): string | null {
  const m = location.pathname.match(/^\/welcome\/([0-9a-f-]{36})\/?$/i);
  return m ? m[1] : null;
}

/** A user opened their set-password link from the admin's invite email. */
export function WelcomeScreen({ token, onDone }: { token: string; onDone: () => void }) {
  const [peek, setPeek] = useState<UserInvitePeek | null | undefined>(undefined);
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { backend.peekUserInvite(token).then(setPeek).catch(() => setPeek(null)); }, [token]);

  async function submit(e: FormEvent) {
    e.preventDefault(); setErr('');
    if (pw.length < 8) { setErr(t('auth.err.short')); return; }
    if (pw !== pw2) { setErr(t('auth.err.mismatch')); return; }
    setBusy(true);
    try {
      const { email } = await backend.acceptUserInvite(token, pw);
      await backend.signOut().catch(() => {});
      await backend.signIn(email, pw);
      history.replaceState(null, '', '/');
      onDone();
    } catch (ex) { setErr(tErr(ex)); } finally { setBusy(false); }
  }

  if (peek === undefined) return <Spinner />;
  const leave = () => { history.replaceState(null, '', '/'); onDone(); };
  if (!peek || peek.status !== 'open') {
    return (
      <AuthCard>
        <h1 className="bh-h1 bh-auth-title">{t('welcome.title')}</h1>
        <Notice tone="warn">{!peek ? t('welcome.invalid') : peek.status === 'used' ? t('welcome.used') : t('welcome.expired')}</Notice>
        <button className="bh-btn bh-btn-primary bh-full" onClick={leave}>{t('auth.signIn')}</button>
      </AuthCard>
    );
  }
  return (
    <AuthCard>
      <h1 className="bh-h1 bh-auth-title">{t('welcome.hello', { name: peek.name || '' })}</h1>
      <p className="bh-auth-sub">{t('welcome.sub')}</p>
      <form className="bh-form" onSubmit={submit}>
        <Field label={t('auth.email')}><input type="email" value={peek.email} readOnly autoComplete="username" /></Field>
        <Field label={t('welcome.choose')}><PasswordInput value={pw} onChange={setPw} autoFocus strength /></Field>
        <Field label={t('auth.passwordAgain')}><PasswordInput value={pw2} onChange={setPw2} matchWith={pw} /></Field>
        <ErrorText>{err}</ErrorText>
        <button type="submit" className="bh-btn bh-btn-primary bh-full bh-btn-lg" disabled={busy}>{busy ? t('common.loading') : t('welcome.save')}</button>
      </form>
      <div className="bh-auth-foot"><a className="bh-link" href="/manual" target="_blank" rel="noreferrer">{t('auth.howItWorks')}</a></div>
    </AuthCard>
  );
}
